import { Badge, Box, Card, Container, Flex, Heading, Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import AuthForm from "../components/auth/AuthForm";
import CheckEmailView from "../components/auth/CheckEmailView";
import DesktopAuthFlow from "../components/auth/flows/DesktopAuthFlow";
import DeviceVerificationFlow from "../components/auth/flows/DeviceVerificationFlow";
import McpConnectorFlow from "../components/auth/flows/McpConnectorFlow";
import McpDashboardRedirectFlow from "../components/auth/flows/McpDashboardRedirectFlow";
import ServiceNoticeBanner from "../components/dashboard/ServiceNoticeBanner";
import Alert from "../components/shared/Alert";
import Footer from "../components/shared/Footer";
import LoadingIndicator from "../components/shared/LoadingIndicator";
import { useAuth } from "../contexts/AuthContext";
import { usePostHog } from "../hooks/usePostHog";
import { useServiceNotice } from "../hooks/useServiceNotice";
import { identifyPostHogUser, resetPostHogUser, setUserProperties } from "../utils/posthogConfig";
import { clearSentryUser, setSentryUser } from "../utils/sentryConfig";

/**
 * Auth Landing Component
 *
 * Encapsulates the main logic for the auth entry point:
 * - MCP connector authentication
 * - Desktop (Electron) app authentication
 * - Landing / Login page
 */
export default function AuthPage() {
    const { user, loading, isSessionChecked, needsMarketingConsent, signInWithEmail, signUpWithEmail, signInWithGoogle, refresh } = useAuth();
    const { track } = usePostHog();
    const serviceNotice = useServiceNotice();
    const navigate = useNavigate();
    const [pendingConfirmEmail, setPendingConfirmEmail] = useState<string | null>(null);
    // Initialize from the hash so the first render already shows the loader —
    // avoids a flicker of the auth form before the hash useEffect runs.
    const [confirmingSignup, setConfirmingSignup] = useState(() => {
        const hash = typeof window !== "undefined" ? window.location.hash : "";
        return hash.includes("access_token") && hash.includes("type=signup");
    });
    const [confirmError, setConfirmError] = useState<string | null>(() => {
        const error = new URLSearchParams(window.location.search).get("error");
        if (error === "region_unavailable") {
            return "The service is not available in your region.";
        }
        return null;
    });
    // Guard against StrictMode double-fire and stale re-runs of the hash effect.
    const hashHandled = useRef(false);

    // Get URL parameters
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const isDesktop = params.get("is_desktop") === "true";
    const verifyDevice = params.get("verify_device") === "true";
    const isMcpDashboard = params.get("mcp_dashboard") === "true";

    console.log("🔍 [AUTH] Auth-only app params:", {
        sessionId,
        isDesktop,
        verifyDevice,
        isMcpDashboard,
        user: user?.email,
        loading,
        isSessionChecked,
    });

    // Handle email confirmation hash: Supabase redirects here with
    // #access_token=...&refresh_token=...&type=signup after the user
    // clicks the confirmation link.
    useEffect(() => {
        if (hashHandled.current) {
            return;
        }

        const hash = window.location.hash;
        if (!hash || !hash.includes("access_token")) {
            return;
        }

        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const type = hashParams.get("type");
        const hashError = hashParams.get("error");
        const hashErrorDescription = hashParams.get("error_description");

        if (hashError) {
            hashHandled.current = true;
            setConfirmError(hashErrorDescription || hashError);
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
            return;
        }

        if (!accessToken || type !== "signup") {
            return;
        }

        hashHandled.current = true;
        setConfirmingSignup(true);
        (async () => {
            try {
                const response = await fetch("/auth/backend/session/create", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        access_token: accessToken,
                        refresh_token: refreshToken || accessToken,
                        // type=signup confirmation link → mark as fresh signup so the
                        // marketing-consent page is shown after the session is established.
                        is_signup: true,
                    }),
                });

                if (!response.ok) {
                    let serverMsg = "Failed to establish session from confirmation token";
                    try {
                        const errData = await response.json();
                        if (errData?.error_description) {
                            serverMsg = errData.error_description;
                        }
                    } catch {
                        /* non-JSON response */
                    }
                    throw new Error(serverMsg);
                }

                const sessionData = await response.json().catch(() => ({}));
                track("auth_email_confirmation_completed", {
                    method: "email",
                    user_id: sessionData?.user?.id,
                });

                // Restore in-progress flow params (session_id, is_desktop, etc.) stashed
                // at signup time — Supabase's email link drops the query string.
                // Stash format: JSON {search, ts}; discard if older than 24h.
                const currentSearch = window.location.search;
                let restoredSearch: string | null = null;
                try {
                    const stashed = localStorage.getItem("pendingConfirmRedirect");
                    if (stashed && !currentSearch) {
                        try {
                            const parsed = JSON.parse(stashed) as {
                                search?: string;
                                ts?: number;
                            };
                            const fresh = typeof parsed?.ts === "number" && Date.now() - parsed.ts < 24 * 60 * 60 * 1000;
                            if (fresh && parsed.search) {
                                restoredSearch = parsed.search;
                            }
                        } catch {
                            /* legacy raw-string format — discard */
                        }
                    }
                    localStorage.removeItem("pendingConfirmRedirect");
                } catch {
                    /* storage disabled */
                }

                await refresh();

                if (restoredSearch) {
                    // Hard navigate so URLSearchParams in the dispatcher re-reads params.
                    window.location.replace(window.location.pathname + restoredSearch);
                    return;
                }

                window.history.replaceState(null, "", window.location.pathname + currentSearch);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : "Unknown error";
                console.error("[AUTH] Failed to confirm signup", err);
                track("auth_email_confirmation_failed", {
                    method: "email",
                    error_message: errorMessage,
                });
                setConfirmError("Confirmation link is invalid or expired. Please sign in.");
            } finally {
                setConfirmingSignup(false);
            }
        })();
    }, [
        refresh,
        track,
    ]);

    // Track user in Sentry and PostHog when authentication state changes
    useEffect(() => {
        if (user) {
            // User logged in - set Sentry user context (ID only for privacy)
            setSentryUser({
                id: user.id,
            });

            // Identify user in PostHog
            identifyPostHogUser(user.id, {
                email: user.email,
                auth_provider: user.app_metadata?.provider || "email",
            });

            // Set user properties for analysis
            setUserProperties({
                devices_connected_count: 0, // Initial value, updated by useUserDevices
                last_login: new Date().toISOString(),
            });
        } else {
            // User logged out - clear Sentry user context and reset PostHog
            clearSentryUser();
            resetPostHogUser();
        }
    }, [
        user,
    ]);

    // Show loading state
    if (!isSessionChecked || confirmingSignup) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    fontFamily: "system-ui, sans-serif",
                }}
            >
                <LoadingIndicator />
            </div>
        );
    }

    // Post-signup: waiting for user to click confirmation email
    if (!user && pendingConfirmEmail) {
        return (
            <CheckEmailView
                email={pendingConfirmEmail}
                onBackToSignIn={() => {
                    try {
                        localStorage.removeItem("pendingConfirmRedirect");
                    } catch {
                        /* storage disabled */
                    }
                    setPendingConfirmEmail(null);
                }}
            />
        );
    }

    // --- Dispatcher Logic ---

    // 0. Fresh signup: collect marketing consent before any other flow. Preserve
    // the in-progress params (session_id, is_desktop, etc.) so the original flow
    // resumes once consent is recorded.
    if (user && needsMarketingConsent && window.location.pathname !== "/welcome") {
        navigate(`/welcome${window.location.search}`, {
            replace: true,
        });
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    fontFamily: "system-ui, sans-serif",
                }}
            >
                <LoadingIndicator />
            </div>
        );
    }

    // 1. Desktop Flow: Redirect to Desktop App
    if (user && sessionId) {
        if (isDesktop) {
            return <DesktopAuthFlow sessionId={sessionId} user={user} loading={loading} />;
        } else {
            return <McpConnectorFlow sessionId={sessionId} />;
        }
    }

    // 2. Device Verification Flow: Authenticated user wants to verify a device
    if (user && verifyDevice) {
        return <DeviceVerificationFlow userEmail={user.email} />;
    }

    // 3. MCP Dashboard Redirect
    if (user && isMcpDashboard) {
        return <McpDashboardRedirectFlow />;
    }

    // 3. Default authenticated view: a signed-in user landing on the bare auth
    // domain has nothing to do here, so send them to the dashboard (same redirect
    // as ?mcp_dashboard=true).
    if (user) {
        return <McpDashboardRedirectFlow />;
    }

    // 4. Unauthenticated: Show Login/Signup Form
    return (
        // Fixed to the viewport, as in DashboardShell: body's flex centering
        // would shrink the banner to the card's width.
        <Flex
            direction="column"
            style={{
                position: "fixed",
                inset: 0,
                overflowY: "auto",
            }}
        >
            {serviceNotice && <ServiceNoticeBanner notice={serviceNotice} />}
            <Container
                size="2"
                p="4"
                style={{
                    flex: 1,
                    // The column parent would otherwise stretch the card to full width.
                    alignSelf: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    // Optical centering: a little bottom padding lifts the card just above the
                    // true middle so it doesn't read as sitting low.
                    paddingBottom: "12vh",
                }}
            >
                <Card
                    size="4"
                    style={{
                        width: "100%",
                    }}
                >
                    <Flex
                        direction="column"
                        gap="4"
                        align="center"
                        style={{
                            textAlign: "center",
                            marginBottom: "24px",
                        }}
                    >
                        <img
                            src={logo}
                            alt="Logo"
                            style={{
                                width: 64,
                                height: 64,
                                objectFit: "contain",
                            }}
                        />
                        <Box>
                            <Flex align="center" gap="2" justify="center" mb="2">
                                <Heading size="6">Desktop Commander</Heading>
                                <Badge color="blue" variant="soft">
                                    beta
                                </Badge>
                            </Flex>
                            <Text color="gray" size="2">
                                {isDesktop ? "Login to Desktop Commander" : "Connect to use Desktop Commander Remote MCP"}
                            </Text>
                        </Box>
                    </Flex>

                    {confirmError && (
                        <Box mb="3">
                            <Alert type="error" message={confirmError} onClose={() => setConfirmError(null)} />
                        </Box>
                    )}

                    <Box mt="4">
                        <AuthForm
                            onSignIn={async ({ email, password }) => {
                                await signInWithEmail(email, password);
                            }}
                            onSignUp={async ({ email, password }) => {
                                const result = await signUpWithEmail(email, password);
                                if (result?.requires_confirmation) {
                                    setPendingConfirmEmail(email);
                                }
                            }}
                            onForgotPassword={() => navigate("/forgot-password")}
                            onGoogleSignIn={() => signInWithGoogle(window.location.href)}
                            loading={loading}
                        />
                    </Box>
                </Card>
                <Footer border={false} />
            </Container>
        </Flex>
    );
}
